import { Calendar, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useMemo } from "react";
import {
  calculateYearMaxCount,
  filterDataByYear,
  generateMonthsForYear,
  getMonthLabel,
  MonthCalendar,
} from "@/components/ActivityCalendar";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";
import { getMaxYear, MIN_YEAR } from "./constants";
import type { YearCalendarProps } from "./types";

export const YearCalendar = ({
  selectedYear,
  data,
  onYearChange,
  onDateClick,
  className,
  holidays: externalHolidays,
}: YearCalendarProps) => {
  const t = useTranslate();

  // Use external holidays if provided, otherwise empty record
  // YearCalendar now relies on ListActivityStats API for holiday data
  const holidaysRecord = useMemo(() => {
    if (externalHolidays) {
      return externalHolidays;
    }
    return {};
  }, [externalHolidays]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const yearData = useMemo(() => filterDataByYear(data, selectedYear), [data, selectedYear]);
  const months = useMemo(() => generateMonthsForYear(selectedYear), [selectedYear]);
  const yearMaxCount = useMemo(() => calculateYearMaxCount(yearData), [yearData]);

  const canGoPrev = selectedYear > MIN_YEAR;
  const canGoNext = selectedYear < getMaxYear();
  const isCurrentYear = selectedYear === currentYear;

  const handlePrevYear = () => canGoPrev && onYearChange(selectedYear - 1);
  const handleNextYear = () => canGoNext && onYearChange(selectedYear + 1);
  const handleToday = () => onYearChange(currentYear);

  return (
    <div className={cn("w-full flex flex-col gap-6 p-4 md:p-6 select-none relative", className)}>
      {/* Animated background pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden -z-10 rounded-3xl">
        {/* Top gradient bloom */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-purple-500/5 via-transparent to-amber-500/5 rounded-full blur-3xl" />

        {/* Bottom gradient bloom */}
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-amber-500/5 via-transparent to-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header with enhanced styling */}
      <div className="relative flex items-center justify-between pb-6 px-2 pt-2">
        {/* Year display with icon and gradient */}
        <div className="flex items-center gap-3">
          {/* Icon container */}
          <div className="relative group/year">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-amber-100 dark:from-purple-950/30 dark:to-amber-950/30 rounded-xl blur-md opacity-0 group-hover/year:opacity-100 transition-opacity duration-300" />
            <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-purple-50 to-amber-50 dark:from-purple-950/30 dark:to-amber-950/30 border border-purple-200/50 dark:border-purple-800/30 flex items-center justify-center shadow-lg">
              <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              {/* Floating particle */}
              <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
            </div>
          </div>

          {/* Year number */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-amber-500/10 rounded-lg blur-sm" />
            <h2 className="relative text-3xl font-bold bg-gradient-to-r from-purple-600 to-amber-600 dark:from-purple-400 dark:to-amber-400 bg-clip-text text-transparent tracking-tight leading-none">
              {selectedYear}
            </h2>
          </div>
        </div>

        {/* Control buttons with enhanced styling */}
        <div className="inline-flex items-center gap-2 shrink-0">
          {/* Previous year button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevYear}
              disabled={!canGoPrev}
              aria-label="Previous year"
              className={cn(
                "relative h-9 w-9 p-0 rounded-xl overflow-hidden transition-all duration-300",
                "hover:shadow-md hover:shadow-purple-500/20",
                canGoPrev && "hover:border-purple-500/30",
              )}
            >
              {/* Purple gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-950/30 dark:to-purple-950/10 rounded-lg blur-sm opacity-0 hover:opacity-100 transition-opacity duration-300" />
              <ChevronLeftIcon className="relative w-5 h-5 text-muted-foreground hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-300 hover:-translate-x-0.5" />
            </Button>
          </div>

          {/* Today button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToday}
              disabled={isCurrentYear}
              aria-label={t("common.today")}
              className={cn(
                "relative h-9 px-4 rounded-xl overflow-hidden text-sm font-semibold transition-all duration-300",
                isCurrentYear
                  ? "bg-muted/30 text-muted-foreground cursor-default"
                  : "hover:shadow-md hover:shadow-amber-500/20 hover:border-amber-500/30 text-foreground",
              )}
            >
              {/* Amber gradient on hover (only when active) */}
              {!isCurrentYear && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/30 dark:to-amber-950/10 rounded-lg blur-sm opacity-0 hover:opacity-100 transition-opacity duration-300" />
                </>
              )}
              <span className="relative">{t("common.today")}</span>
            </Button>
          </div>

          {/* Next year button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextYear}
              disabled={!canGoNext}
              aria-label="Next year"
              className={cn(
                "relative h-9 w-9 p-0 rounded-xl overflow-hidden transition-all duration-300",
                "hover:shadow-md hover:shadow-amber-500/20",
                canGoNext && "hover:border-amber-500/30",
              )}
            >
              {/* Amber gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-950/30 dark:to-amber-950/10 rounded-lg blur-sm opacity-0 hover:opacity-100 transition-opacity duration-300" />
              <ChevronRightIcon className="relative w-5 h-5 text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400 transition-all duration-300 hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Month grid with glass cards */}
      <TooltipProvider>
        <div className="w-full animate-fade-in">
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {months.map((month, index) => (
              <div
                key={month}
                className="group relative flex flex-col gap-3 rounded-xl p-4 overflow-hidden transition-all duration-300"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                {/* Glass-card background */}
                <div className="absolute inset-0 glass-card rounded-xl" />

                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-amber-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Month label */}
                <div className="relative flex items-center gap-2 pl-1">
                  <div className="text-xs font-bold bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent uppercase tracking-widest">
                    {getMonthLabel(month)}
                  </div>
                  {/* Decorative dot */}
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500/30 group-hover:bg-purple-500/60 transition-colors duration-300" />
                </div>

                {/* Calendar */}
                <div className="relative">
                  <MonthCalendar
                    month={month}
                    data={yearData}
                    maxCount={yearMaxCount}
                    size="small"
                    onClick={onDateClick}
                    holidays={holidaysRecord}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
};
